import type { BasicDigitalTwin } from 'src/models'
import { isNumeric, isDate } from 'src/utils/type-checks'

/**
 * Compares two arrays as sets, checking if they contain the same elements regardless of order.
 * Elements are type-encoded to distinguish between values of different types (e.g., 1 vs "1").
 *
 * @param a1 - First array of primitive types (number, string, boolean)
 * @param a2 - Second array of primitive types (number, string, boolean)
 * @returns True if both arrays contain the same elements with the same types, false otherwise
 * @throws TypeError if either array is not iterable
 */
export const areArraysEqualSets = (
  a1: (number | string | boolean)[],
  a2: (number | string | boolean)[]
): boolean => {
  const superSet: Record<string, number> = {}
  for (const i of a1) {
    const e = i + typeof i
    superSet[e] = 1
  }

  for (const i of a2) {
    const e = i + typeof i
    if (!superSet[e]) {
      return false
    }
    superSet[e] = 2
  }

  for (const e in superSet) {
    if (superSet[e] === 1) {
      return false
    }
  }

  return true
}

/**
 * Compares two date values and returns a numeric sort indicator.
 * Used internally by dynamicSort for date sorting.
 *
 * @param a - First date value (string, number, or Date object)
 * @param b - Second date value (string, number, or Date object)
 * @returns Negative if a < b, positive if a > b, zero if equal
 */
const sortDate = (a: string | number | Date, b: string | number | Date) => {
  return new Date(a).getTime() - new Date(b).getTime()
}

/**
 * Compares two boolean values and returns a numeric sort indicator.
 * false values are sorted before true values.
 *
 * @param a - First boolean value
 * @param b - Second boolean value
 * @returns -1 if a is true and b is false, 1 if a is false and b is true, 0 otherwise
 */
const sortBoolean = (a: boolean, b: boolean) => {
  if (a && !b) return -1
  if (!a && b) return 1
  return 0
}

/**
 * Creates a sort comparator function for objects by a specific property.
 * Handles numeric, date, boolean, and string values, with support for null/undefined sorting.
 *
 * @param key - The property key to sort by
 * @param descending - Whether to sort in descending order (default: false)
 * @returns A comparator function for use with Array.sort()
 */
const dynamicSort = (key: string, descending = false) => {
  const dir = descending ? -1 : 1,
    val = (v: { [x: string]: unknown }) => v[key]
  return (a: { [x: string]: unknown }, b: { [x: string]: unknown }) => {
    const A = val(a)
    const B = val(b)

    if (A === null || A === void 0) {
      return -1 * dir
    }
    if (B === null || B === void 0) {
      return Number(dir)
    }
    if (isNumeric(A) && isNumeric(B)) {
      return (A - B) * dir
    }
    if (isDate(A) && isDate(B)) {
      return sortDate(A, B) * dir
    }
    if (typeof A === 'boolean' && typeof B === 'boolean') {
      return sortBoolean(A, B) * dir
    }

    if (typeof A === 'object' || typeof B === 'object') {
      return 0
    }

    const aStr = String(A).toLocaleLowerCase()
    const bStr = String(B).toLocaleLowerCase()

    if (aStr < bStr) {
      return -1 * dir
    }
    if (aStr > bStr) {
      return dir
    }
    return 0
  }
}

/**
 * Creates a sort comparator function for primitive values.
 * Uses dynamicSort internally to handle different value types.
 *
 * @param descending - Whether to sort in descending order (default: false)
 * @returns A comparator function for use with Array.sort()
 */
export const dynamicPrimitiveSort = (descending = false) => {
  return (a: unknown, b: unknown) => dynamicSort('key', descending)({ key: a }, { key: b })
}

/**
 * Creates a sort comparator function for objects by multiple property values.
 * Iterates through keys in order until a non-equal comparison is found.
 *
 * @param keys - Array of property keys to sort by, in priority order
 * @param descending - Whether to sort in descending order (default: false)
 * @returns A comparator function for use with Array.sort()
 */
export const dynamicSortMultiple = (keys: string[], descending = false) => {
  /*
   * save the arguments object as it will be overwritten
   * note that arguments object is an array-like object
   * consisting of the names of the properties to sort by
   */
  return (a: { [x: string]: unknown }, b: { [x: string]: unknown }) => {
    /* try getting a different result from 0 (equal)
     * as long as we have extra properties to compare
     */
    let result = 0
    for (const key of keys) {
      result = dynamicSort(key, descending)(a, b)
      if (result !== 0) break
    }
    return result
  }
}

/**
 * Splits an array into chunks of a specified size.
 * Useful for processing large datasets in batches to avoid blocking the client.
 * Consider using with setTimeout or Promise for non-blocking iteration.
 *
 * @typeParam T - The element type of the input array
 * @param arr - The array to split into chunks
 * @param size - The maximum size of each chunk
 * @returns An array of arrays, each containing up to 'size' elements
 */
export const splitInChunks = <T>(arr: Array<unknown>, size: number): Array<T[]> => {
  const chunks: Array<T[]> = []
  let i = 0
  const n = arr.length
  while (i < n) {
    const end = i + size
    chunks.push(arr.slice(i, end) as T[])
    i = end
  }
  return chunks
}

/**
 * Moves an item in an array up or down and returns the modified array.
 * This function mutates the original array.
 *
 * @param arr - The array containing the item to move
 * @param index - The index of the item to move
 * @param direction - Direction to move ('up' or 'down', default: 'down')
 * @returns The modified array with the item moved
 */
export const arrayMoveItem = <T>(
  arr: Array<T>,
  index: number,
  direction: 'up' | 'down' = 'down'
) => {
  const el = arr[index]
  if (direction === 'up' && index > 0) {
    arr[index] = arr[index - 1]
    arr[index - 1] = el
  } else if (direction === 'down' && index !== -1 && index < arr.length - 1) {
    arr[index] = arr[index + 1]
    arr[index + 1] = el
  }
  return arr
}

/**
 * Sort callback function for Features and FeatureQueries.
 * Sorts by $order property, with items lacking $order sorted last (assigned Infinity).
 *
 * @param a - First BasicDigitalTwin to compare
 * @param b - Second BasicDigitalTwin to compare
 * @returns Negative if a should come before b, positive if after, 0 if equal
 */
export const sortCallback = (
  a: BasicDigitalTwin & { $order?: number },
  b: BasicDigitalTwin & { $order?: number }
) => {
  const aNum = a.$order ?? Infinity
  const bNum = b.$order ?? Infinity
  return aNum - bNum
}
